import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Zap, LayoutDashboard, ArrowRight, MessageSquareWarning, CreditCard } from 'lucide-react';

const paths = [
  {
    icon: Zap,
    step: '01',
    title: 'Automatisera ett problem',
    desc: 'Repetitivt arbete som tar tid varje vecka? Vi kartlägger processen och bygger ett flöde som sköter det automatiskt.',
    cta: 'Se automationer',
    href: '/automations',
    gradient: 'from-amber-400 to-orange-500',
    glow: 'rgba(251,146,60,0.25)',
    border: 'hover:border-amber-300',
  },
  {
    icon: LayoutDashboard,
    step: '02',
    title: 'Bygga ett system',
    desc: 'Behöver ni ett verktyg, en chatbot eller en SaaS-produkt? Vi designar och bygger det från grunden — snabbt och utan onödig komplexitet.',
    cta: 'Se tjänster',
    href: '/ai-consulting',
    gradient: 'from-indigo-500 to-violet-600',
    glow: 'rgba(99,102,241,0.25)',
    border: 'hover:border-indigo-300',
  },
  {
    icon: MessageSquareWarning,
    step: '03',
    title: 'Använd en färdig lösning',
    desc: 'Vill ni komma igång direkt? Klagobot och Swishkassa är egna produkter ni kan använda utan ett eget projekt.',
    cta: 'Se produkter',
    href: '/klagobot',
    gradient: 'from-violet-500 to-purple-600',
    glow: 'rgba(139,92,246,0.25)',
    border: 'hover:border-violet-300',
    extraLinks: [
      { label: 'Klagobot', href: '/klagobot', icon: MessageSquareWarning },
      { label: 'Swishkassa', href: '/swishkassa', icon: CreditCard },
    ],
  },
];

const PathsSection = () => (
  <section className="py-24 bg-white">
    <div className="container mx-auto px-6">

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="text-center mb-16"
      >
        <span className="inline-block text-xs font-semibold tracking-[0.18em] uppercase text-brand-indigo mb-4">
          Kom igång
        </span>
        <h2 className="text-3xl sm:text-[42px] font-extrabold text-text-primary tracking-[-0.02em] leading-tight mb-4">
          Så kan vi hjälpa dig
        </h2>
        <p className="text-text-secondary max-w-lg mx-auto leading-relaxed">
          Välj det som passar er situation bäst — eller kontakta oss om du är osäker.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
        {paths.map((p, i) => {
          const Icon = p.icon;
          return (
            <motion.div
              key={p.step}
              whileHover={{ y: -5 }}
              className={`group relative bg-white rounded-2xl border border-border-light p-7 flex flex-col transition-all duration-300 ${p.border}`}
              style={{ boxShadow: '0 2px 8px rgba(99,102,241,0.07)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = `0 16px 48px ${p.glow}`; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 2px 8px rgba(99,102,241,0.07)'; }}
            >
              {/* Step number */}
              <span className="text-[10px] font-bold tracking-widest uppercase text-text-muted mb-4 block">{p.step}</span>

              {/* Icon */}
              <div className={`inline-flex w-11 h-11 rounded-xl bg-gradient-to-br ${p.gradient} items-center justify-center mb-5 shadow-md`}
                style={{ boxShadow: `0 4px 12px ${p.glow}` }}>
                <Icon className="w-5 h-5 text-white" />
              </div>

              {/* Title */}
              <h3 className="text-[18px] font-bold text-text-primary mb-3 leading-snug">{p.title}</h3>

              {/* Description */}
              <p className="text-[14px] text-text-secondary leading-relaxed flex-1 mb-6">{p.desc}</p>

              {/* Extra product links for path 3 */}
              {p.extraLinks && (
                <div className="flex flex-col gap-2 mb-5">
                  <Link
                    to="/klagobot"
                    className="inline-flex items-center justify-center gap-2 text-[13px] font-semibold text-white bg-gradient-to-r from-indigo-500 to-violet-600 rounded-lg px-4 py-2.5 hover:opacity-90 transition-opacity shadow-sm"
                  >
                    <MessageSquareWarning className="w-3.5 h-3.5" />
                    Testa Klagobot
                  </Link>
                  <Link
                    to="/swishkassa"
                    className="inline-flex items-center justify-center gap-2 text-[13px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5 hover:bg-emerald-100 transition-colors"
                  >
                    <CreditCard className="w-3.5 h-3.5" />
                    Se Swishkassa
                  </Link>
                </div>
              )}

              {/* CTA */}
              <Link
                to={p.href}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-indigo group-hover:opacity-80 transition-opacity"
              >
                {p.cta}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
              </Link>

              {/* Top accent line on hover */}
              <div className={`absolute top-0 left-6 right-6 h-[2px] rounded-b-full bg-gradient-to-r ${p.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
            </motion.div>
          );
        })}
      </div>

    </div>
  </section>
);

export default PathsSection;
